import React, { Component } from 'react'
import PropTypes from 'prop-types'
import Select from 'react-select-plus'
import { Input } from '@driftt/tide-core-legacy'
import { api } from 'app/modules/sfdc/api'
import { find } from 'ramda'
import './style.styl'

const MISSING_CAMPAIGN = 'The previously used campaign is no longer available.'
const UNKNOWN_ERROR = 'An unknown error occurred. If this problem persists, please chat with us.'
const SEARCH_DEBOUNCE_TIMEOUT = 500


class SalesforceCampaignSelect extends Component {

  static propTypes = {
    defaultSelectLabelText: PropTypes.string.isRequired,
    description: PropTypes.string.isRequired,
    fetchSelectedCampaign: PropTypes.func.isRequired,
    setSelectedCampaign: PropTypes.func.isRequired
  }

  constructor(props) {
    super(props)
    this.state = {
      isLoading: true,
      hasError: false,
      selectValue: null,
      selectLabel: null,
      selectOptions: [],
      responseOptions: []
    }
  }

  componentDidMount() {
    this.getInitialValue()
  }


  componentDidUpdate(prevProps, prevState) {
    if (prevProps.setSelectedCampaign.name === this.props.setSelectedCampaign.name) {
      return
    }
    if (prevProps.fetchSelectedCampaign() != this.props.fetchSelectedCampaign()) {
      this.getInitialValue()
    }
  }


  fetchCampaignById = (campaignId) => {
    const onError = (err) => {
      if (err.status == 404) {
        this.handleMissingCampaign()
      } else {
        this.handleUnknownError()
      }
    }

    return api.fetchSalesforceCampaignById(campaignId)
      .then(this.handleFetchedCampaign)
      .catch(onError)
  }


  resolveOptions = (options) => {
    const resolvedOptions = options.salesforceCampaigns.map(campaign => ({ Name: campaign.Name, Id: campaign.Id }))
    this.setState({
      selectOptions: resolvedOptions
    })
  }

  getInitialValue = async () => {
    const { fetchSelectedCampaign } = this.props

    await this.fetchAllSalesforceCampaigns()

    const onCampaignIdPresent = (campaignId) => {
      const onStateSet = () => this.fetchCampaignById(campaignId)

      this.setState({
        selectValue: campaignId,
        isLoading: true
      }, onStateSet)
    }

    const onCampaignIdAbsent = () => {
      this.setState({
        selectValue: null,
        isLoading: false
      })
    }

    const onSelectedCampaignIdFetched = (data) => {
      data.campaignId
        ? onCampaignIdPresent(data.campaignId)
        : onCampaignIdAbsent()
    }

    fetchSelectedCampaign()
      .then(onSelectedCampaignIdFetched)
      .catch(this.handleUnknownError)
  }

  fetchAllSalesforceCampaigns = async () => {
    try {
      const options = await api.fetchAllSalesforceCampaigns()
      this.resolveOptions(options)
    }
    catch (e) {
      console.log(e)
    }
  }

  handleFetchedCampaign = (campaign) => {
    const existsInList = find((campaignId) => campaignId.Id === campaign.Id, this.state.selectOptions)
    const nextState = !!existsInList ? {
      selectLabel: campaign.Name,
      selectValue: campaign.Id,
      isLoading: false,
      hasError: false
    } : {
        selectOptions: this.state.selectOptions.concat([campaign]),
        selectLabel: campaign.Name,
        selectValue: campaign.Id,
        isLoading: false,
        hasError: false
      }
    this.setState(nextState)
  }

  handleMissingCampaign = () => {
    this.setState({
      isLoading: false,
      hasError: true,
      selectValue: null,
      errorText: MISSING_CAMPAIGN
    })
  }

  handleUnknownError = () => {
    this.setState({
      isLoading: false,
      hasError: true,
      errorText: UNKNOWN_ERROR
    })
  }

  onChange = (campaign) => {
    const { setSelectedCampaign } = this.props

    const onDone = () => {
      this.setState({
        isLoading: false,
        hasError: false
      })
    }

    const onStateSet = () =>
      setSelectedCampaign({ campaignId: campaign.Id })
        .then(onDone)

    const existsInList = find((campaignId) => campaignId.Id === campaign.Id, this.state.selectOptions)
    const nextState = !!existsInList || !campaign.Name ? {
      selectValue: campaign.Id,
      selectLabel: campaign.Name,
      isLoading: true
    } : {
        selectOptions: this.state.selectOptions.concat([campaign]),
        selectValue: campaign.Id,
        selectLabel: campaign.Name,
        isLoading: true
      }
    this.setState(
      nextState
      , onStateSet)
  }

  searchSalesforceCampaigns = (query) => {
    if (this.state.selectOptions.length < 500 || query.length <= 2) {
      this.setState({ responseOptions: [] })
      return;
    }

    // salesforce wants queries of length > 2 or it won't return anything
    if (query.length > 2) {
      this.debouncedSearch(query)
    }
    return query
  }

  searchForCampaigns = (query) => {
    const onDone = (response) => {
      this.setState({
        responseOptions: response.salesforceCampaigns,
        isLoading: false
      })
    }
    const fetchCampaigns = () =>
      api.searchSalesforceCampaigns(query)
        .then(onDone)
        .catch(this.handleUnknownError)

    this.setState({ isLoading: true }, fetchCampaigns)
  }

  debouncedSearch = _.debounce(this.searchForCampaigns, SEARCH_DEBOUNCE_TIMEOUT)

  resolveDefaultSelectOption = () => {
    return { Id: null, Name: null }
  }

  resolveSelectOptions = () => {
    const { selectOptions } = this.state
    const defaultSelectOption = this.resolveDefaultSelectOption()
    return selectOptions.concat(defaultSelectOption)
  }

  handleToggleChange = (change) => {
    this.props.onToggleChange({ campaignId: this.state.selectValue }, change)
  }

  render() {
    const { description, defaultSelectLabelText, type, toggleStatus } = this.props
    const { selectValue, selectLabel, isLoading, hasError, errorText, responseOptions } = this.state
    const defaultSelectOption = this.resolveDefaultSelectOption()
    const selectOptions = responseOptions.length > 0 ? responseOptions : this.state.selectOptions
    return (
      <div className="salesforce-campaign-select-container">
        <div className="advanced-integration-toggle-header">
          <p className="salesforce-campaign-select-description">{description}</p>
        </div>
        <Select
          className="salesforce-campaign-select-input"
          labelKey="Name"
          valueKey="Id"
          autosize={false}
          placeholder={defaultSelectLabelText}
          value={selectValue}
          label={selectLabel}
          options={selectOptions}
          isLoading={isLoading}
          onChange={this.onChange}
          resetValue={defaultSelectOption}
          onInputChange={this.searchSalesforceCampaigns}
        />
        {hasError &&
          <p className="salesforce-campaign-error-text">
            {errorText}
          </p>
        }
        <div className="salesforce-campaign-mapping-net-new-checkbox">
          <Input
            onChange={this.handleToggleChange}
            type="checkbox"
            label={`Only associate net new ${type} with this campaign`}
            checked={toggleStatus}
          />
        </div>
      </div>
    )
  }
}

export default SalesforceCampaignSelect


// WEBPACK FOOTER //
// ./src/scripts/components/Integration/pages/SalesforceConfig/SalesforceCampaignSelect.js